import { clearAllState, exportStateJSON, importStateJSON } from '@core/persistence';
import { resetAllState, settingsActions } from '@core/store';
import { getState } from '@core/store';
import { signOut, updatePassword } from '@services/auth';
import { notificationsSupported, requestPermission } from '@services/notifications';
import { forceSyncNow } from '@services/sync';
import { toast } from '@ui/components/Toast';
import { navigate } from '@ui/lib/router';
import { useAuth, useSync } from '@ui/lib/useAuth';
import { useStore } from '@ui/lib/useStore';
import { useRef, useState } from 'preact/hooks';

export function SettingsScreen() {
  const settings = useStore((s) => s.settings);
  const { user, ready } = useAuth();
  const sync = useSync();
  const importInputRef = useRef<HTMLInputElement>(null);
  const [newPass, setNewPass] = useState('');
  const [changingPass, setChangingPass] = useState(false);

  async function submitNewPassword() {
    if (newPass.length < 6) {
      toast('A senha precisa de pelo menos 6 caracteres', 'err');
      return;
    }
    setChangingPass(true);
    const { error } = await updatePassword(newPass);
    setChangingPass(false);
    if (error) {
      toast(error, 'err', 5000);
      return;
    }
    setNewPass('');
    toast('✓ Senha definida! Use-a no próximo login.', 'ok', 4000);
  }

  const upd = (patch: Partial<typeof settings>) => settingsActions.update(patch);

  function handleImportFile(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      const result = importStateJSON(String(reader.result || ''));
      if (result.ok) {
        toast(
          `✓ Backup importado: ${result.contents} conteúdos · ${result.sessions} sessões · ${result.goals} metas`,
          'ok',
          4000,
        );
        setTimeout(() => window.location.reload(), 800);
      } else {
        toast(`Falha no import: ${result.error}`, 'err', 5000);
      }
    };
    reader.readAsText(file);
  }

  return (
    <div>
      <div class="main-head">
        <div>
          <h1 class="main-title">Configurações</h1>
          <div class="main-sub">preferências locais</div>
        </div>
      </div>

      <div class="grid-2">
        <div class="panel">
          <div class="panel-title">
            ▰ <b>POMODORO</b>
          </div>
          <div class="field">
            <label>Duração do foco (min)</label>
            <input
              class="input"
              type="number"
              min="5"
              max="90"
              value={settings.pomodoro_focus_min}
              onInput={(e) =>
                upd({
                  pomodoro_focus_min:
                    Number.parseInt((e.target as HTMLInputElement).value, 10) || 25,
                })
              }
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div class="field">
              <label>Pausa curta (min)</label>
              <input
                class="input"
                type="number"
                min="1"
                max="30"
                value={settings.pomodoro_short_break_min}
                onInput={(e) =>
                  upd({
                    pomodoro_short_break_min:
                      Number.parseInt((e.target as HTMLInputElement).value, 10) || 5,
                  })
                }
              />
            </div>
            <div class="field">
              <label>Pausa longa (min)</label>
              <input
                class="input"
                type="number"
                min="5"
                max="60"
                value={settings.pomodoro_long_break_min}
                onInput={(e) =>
                  upd({
                    pomodoro_long_break_min:
                      Number.parseInt((e.target as HTMLInputElement).value, 10) || 15,
                  })
                }
              />
            </div>
          </div>
          <div class="field">
            <label>Ciclos até pausa longa</label>
            <input
              class="input"
              type="number"
              min="2"
              max="8"
              value={settings.pomodoro_cycles_before_long}
              onInput={(e) =>
                upd({
                  pomodoro_cycles_before_long:
                    Number.parseInt((e.target as HTMLInputElement).value, 10) || 4,
                })
              }
            />
          </div>
          <div class="field">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={settings.auto_start_break}
                onChange={(e) => upd({ auto_start_break: (e.target as HTMLInputElement).checked })}
              />
              iniciar pausas automaticamente
            </label>
          </div>
          <div class="field">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={settings.sound_enabled}
                onChange={(e) => upd({ sound_enabled: (e.target as HTMLInputElement).checked })}
              />
              som ao fim de cada ciclo
            </label>
          </div>
          <div class="field">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={settings.notifications_enabled}
                disabled={!notificationsSupported()}
                onChange={async (e) => {
                  const on = (e.target as HTMLInputElement).checked;
                  if (!on) {
                    upd({ notifications_enabled: false });
                    return;
                  }
                  const granted = await requestPermission();
                  if (!granted) {
                    (e.target as HTMLInputElement).checked = false;
                    toast('Permissão de notificação negada pelo browser', 'err', 4000);
                    return;
                  }
                  upd({ notifications_enabled: true });
                  toast('✓ Notificações ativas: sessão chegando + fim de ciclo', 'ok', 4000);
                }}
              />
              notificações do sistema (sessão em 5 min · fim de ciclo)
              {!notificationsSupported() && (
                <span class="tag" style={{ fontSize: '9px' }}>
                  sem suporte neste browser
                </span>
              )}
            </label>
          </div>
        </div>

        <div class="panel">
          <div class="panel-title">
            ▰ <b>PLANO SEMANAL</b>
          </div>
          <div class="field">
            <label>Capacidade semanal (horas)</label>
            <input
              class="input"
              type="number"
              min="1"
              max="80"
              value={settings.weekly_capacity_h}
              onInput={(e) =>
                upd({
                  weekly_capacity_h:
                    Number.parseInt((e.target as HTMLInputElement).value, 10) || 12,
                })
              }
            />
            <div
              style={{
                fontSize: '12px',
                color: 'var(--text-dim)',
                marginTop: '6px',
                fontFamily: 'var(--f-mono)',
              }}
            >
              usado pelo planner automático (F1 · em breve)
            </div>
          </div>
        </div>

        <div class="panel" style={{ gridColumn: '1 / -1' }}>
          <div class="panel-title">
            ▰ <b>CONTA & SINCRONIZAÇÃO</b>
          </div>
          {!ready ? null : user ? (
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap',
                  marginBottom: '12px',
                }}
              >
                <span class="tag green">✓ LOGADO</span>
                <span
                  style={{ fontFamily: 'var(--f-mono)', fontSize: '13px', color: 'var(--text)' }}
                >
                  {user.email}
                </span>
                <span
                  class={`tag ${sync.status === 'synced' ? 'green' : sync.status === 'error' ? '' : 'amber'}`}
                  style={sync.status === 'error' ? { color: 'var(--red)' } : undefined}
                  title={sync.detail || ''}
                >
                  {sync.status === 'synced'
                    ? 'SYNC OK'
                    : sync.status === 'syncing'
                      ? 'SINCRONIZANDO…'
                      : sync.status === 'error'
                        ? 'ERRO DE SYNC'
                        : 'SYNC OFF'}
                </span>
                {sync.last_sync && (
                  <span
                    style={{
                      fontFamily: 'var(--f-mono)',
                      fontSize: '10px',
                      color: 'var(--text-dim)',
                    }}
                  >
                    último: {new Date(sync.last_sync).toLocaleTimeString('pt-BR')}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  class="btn"
                  onClick={async () => {
                    await forceSyncNow();
                    toast('Sincronização forçada', 'ok');
                  }}
                >
                  ↻ SINCRONIZAR AGORA
                </button>
                <button
                  class="btn danger"
                  onClick={() => {
                    if (confirm('Sair da conta? Os dados continuam neste browser e na nuvem.'))
                      signOut();
                  }}
                >
                  SAIR DA CONTA
                </button>
              </div>

              <div
                style={{
                  marginTop: '16px',
                  paddingTop: '14px',
                  borderTop: '1px dashed var(--border-soft)',
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--f-mono)',
                    fontSize: '10px',
                    color: 'var(--text-dim)',
                    letterSpacing: '1.5px',
                    marginBottom: '8px',
                  }}
                >
                  DEFINIR / ALTERAR SENHA
                </div>
                <div
                  style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}
                >
                  <input
                    class="input"
                    type="password"
                    placeholder="nova senha (mín. 6)"
                    style={{ maxWidth: '240px' }}
                    value={newPass}
                    disabled={changingPass}
                    onInput={(e) => setNewPass((e.target as HTMLInputElement).value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitNewPassword();
                    }}
                  />
                  <button class="btn sm" disabled={changingPass} onClick={submitNewPassword}>
                    {changingPass ? 'SALVANDO...' : 'SALVAR SENHA'}
                  </button>
                </div>
                <div
                  style={{
                    marginTop: '6px',
                    fontFamily: 'var(--f-mono)',
                    fontSize: '10px',
                    color: 'var(--text-dim)',
                  }}
                >
                  ▸ entrou pelo link do email? define aqui uma senha pros próximos logins
                </div>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '13.5px', color: 'var(--text-soft)', marginBottom: '12px' }}>
                Você está no modo local — os dados ficam só neste browser. Entre para sincronizar
                entre dispositivos e ter backup automático na nuvem.
              </p>
              <button class="btn gold" onClick={() => navigate('login')}>
                ⚡ ENTRAR / SINCRONIZAR
              </button>
            </div>
          )}
        </div>

        <div class="panel" style={{ gridColumn: '1 / -1' }}>
          <div class="panel-title">
            ▰ <b>DADOS</b>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              class="btn"
              onClick={() => {
                const json = exportStateJSON(getState());
                const blob = new Blob([json], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `robox-backup-${new Date().toISOString().slice(0, 10)}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                toast('Backup baixado', 'ok');
              }}
            >
              ↓ EXPORTAR BACKUP
            </button>
            <button class="btn gold" onClick={() => importInputRef.current?.click()}>
              ↑ IMPORTAR BACKUP
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept=".json,application/json"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (!file) return;
                if (
                  !confirm(
                    'Importar backup SUBSTITUI todos os dados atuais deste browser. Continuar?',
                  )
                ) {
                  (e.target as HTMLInputElement).value = '';
                  return;
                }
                handleImportFile(file);
                (e.target as HTMLInputElement).value = '';
              }}
            />
            <button
              class="btn danger"
              onClick={() => {
                if (
                  !confirm(
                    'Limpar TODOS os dados? Conteúdos, sessões, metas, tudo. Sem volta. Confirma?',
                  )
                )
                  return;
                if (!confirm('Confirma DE VERDADE? Melhor exportar backup antes.')) return;
                clearAllState();
                resetAllState();
                toast('Tudo limpo', 'warn');
                setTimeout(() => window.location.reload(), 500);
              }}
            >
              × LIMPAR TUDO
            </button>
          </div>
          <div
            style={{
              marginTop: '14px',
              fontSize: '12px',
              color: 'var(--text-dim)',
              fontFamily: 'var(--f-mono)',
              lineHeight: 1.6,
            }}
          >
            ▸ logado: sync automático na nuvem (push ~2s após cada mudança).
            <br />▸ sem login: dados só neste browser — exporte backup semanal.
          </div>
        </div>
      </div>
    </div>
  );
}
